import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BellRing, Loader2, Check } from "lucide-react";

const Seg = ({ options, value, onChange, testid }) => (
  <div className="inline-flex flex-wrap gap-1.5" data-testid={testid}>
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          data-testid={`${testid}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-[#0F172A] text-white" : "border border-slate-300 bg-white text-slate-600 hover:border-[#C6A87C]"}`}>
          {o.label}
        </button>
      );
    })}
  </div>
);

export default function AlertSettingsCard() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [bands, setBands] = useState("green");
  const [max, setMax] = useState(4);
  const [minScore, setMinScore] = useState(0);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#alertes") {
      const t = setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlash(true);
        setTimeout(() => setFlash(false), 1600);
      }, 400);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    api.get("/radar/notif-prefs").then((r) => {
      const d = r.data || {};
      setPrefs(d);
      setEnabled(d.email_enabled !== false);
      setBands(d.alert_bands === "green_orange" ? "green_orange" : "green");
      setMax(Math.min(4, Math.max(1, d.alert_max_24h || 4)));
      setMinScore(d.alert_min_score || 0);
    }).catch(() => setPrefs({}));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        in_app_enabled: prefs?.in_app_enabled ?? true,
        notif_min_score: prefs?.notif_min_score ?? 40,
        email_enabled: enabled,
        alert_bands: bands,
        alert_max_24h: max,
        alert_min_score: Math.min(100, Math.max(0, Number(minScore) || 0)),
      };
      const r = await api.put("/radar/notif-prefs", body);
      setPrefs(r.data);
      toast.success(t("account.alerts.saved"));
    } catch {
      toast.error(t("account.alerts.saveFailed"));
    } finally { setBusy(false); }
  };

  if (!prefs) return null;

  return (
    <section ref={ref} id="alertes" className={`mb-6 scroll-mt-6 rounded-2xl border bg-white p-6 transition-all duration-500 ${flash ? "border-[#C6A87C] ring-2 ring-[#C6A87C]/40" : "border-slate-200"}`} data-testid="account-alerts-card">
      <div className="mb-1 flex items-center gap-2">
        <BellRing className="h-5 w-5 text-[#C6A87C]" />
        <h2 className="text-lg font-semibold text-[#0F172A]">{t("account.alerts.title")}</h2>
      </div>
      <p className="mb-5 text-sm text-slate-500">{t("account.alerts.desc")}</p>

      {/* Activer / désactiver */}
      <div className="mb-5 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <span className="text-sm font-medium text-[#0F172A]">{t("account.alerts.enabled")}</span>
        <button
          type="button"
          data-testid="alerts-toggle"
          onClick={() => setEnabled((v) => !v)}
          className={`relative h-6 w-11 rounded-full transition-colors ${enabled ? "bg-[#16a34a]" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>

      <div className={enabled ? "" : "pointer-events-none opacity-50"}>
        {/* Bandes */}
        <div className="mb-5">
          <p className="mb-2 text-sm font-medium text-[#0F172A]">{t("account.alerts.which")}</p>
          <Seg
            testid="alerts-bands"
            value={bands}
            onChange={setBands}
            options={[
              { value: "green", label: `🟢 ${t("account.alerts.greenOnly")}` },
              { value: "green_orange", label: `🟢🟠 ${t("account.alerts.greenOrange")}` },
            ]}
          />
          <p className="mt-2 text-xs text-slate-400">{t("account.alerts.redNote")}</p>
        </div>

        {/* Max par 24h */}
        <div className="mb-5">
          <p className="mb-2 text-sm font-medium text-[#0F172A]">Nombre maximum d'alertes par 24 h</p>
          <Seg
            testid="alerts-max"
            value={max}
            onChange={setMax}
            options={[1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }))}
          />
        </div>

        {/* Score minimum optionnel */}
        <div className="mb-6">
          <p className="mb-1 text-sm font-medium text-[#0F172A]">{t("account.alerts.minScore")} <span className="font-normal text-slate-400">{t("account.alerts.optional")}</span></p>
          <div className="flex items-center gap-3">
            <Input
              type="number" min={0} max={100}
              data-testid="alerts-minscore"
              value={minScore}
              onChange={(e) => setMinScore(e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
              className="h-11 w-28"
            />
            <span className="text-sm text-slate-400">{t("account.alerts.minScoreHint")}</span>
          </div>
        </div>
      </div>

      <Button data-testid="alerts-save" disabled={busy} onClick={save} className="h-11 rounded-full bg-[#0F172A] px-6 hover:bg-[#1e293b]">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} {t("account.alerts.save")}
      </Button>
    </section>
  );
}
